import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Heading,
  Loading,
  Row,
  Screen,
  TextField,
  Title,
} from '../components/ui';
import { api, ApiError } from '../lib/api';
import { formatPhone, timeInZone, timezoneOptions, waMeLink } from '../lib/format';
import { useSession } from '../lib/session';
import type { CustomerSummary } from './Customers';
import './customers.css';

/** One customer's record (§7): details, family, calls. */

interface Customer extends CustomerSummary {
  created_at: string;
}

interface FamilyMember {
  id: string;
  name: string;
  phone: string;
  city: string | null;
}

interface CallRow {
  id: string;
  type: 'nyauta' | 'bulawa';
  started_at: string;
  ended_at: string | null;
  outcome: string | null;
  followup_on: string | null;
  note: string | null;
  staff_name: string;
}

interface Record {
  customer: Customer;
  family: FamilyMember[];
  calls: CallRow[];
  calls_filtered_to_own: boolean;
}

export default function CustomerRecord() {
  const { id } = useParams<{ id: string }>();
  const { state } = useSession();
  const [data, setData] = useState<Record | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const isAdmin = state.status === 'in' && state.me.staff.role === 'admin';

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      setData(await api<Record>(`/api/customers/${id}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that customer.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // No stale screens (§0.5).
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [load]);

  if (error) {
    return (
      <Screen width="wide">
        <Title sub="Customer">Mulaqaat</Title>
        <Card>
          <ErrorState message={error} onRetry={() => void load()} />
        </Card>
        <Row className="crumbs">
          <Link to="/customers" className="btn btn--ghost">
            ← All customers
          </Link>
        </Row>
      </Screen>
    );
  }

  if (!data) {
    return (
      <Screen width="wide">
        <Title sub="Customer">Mulaqaat</Title>
        <Card>
          <Loading label="Loading the record…" />
        </Card>
      </Screen>
    );
  }

  const { customer, family, calls } = data;
  const theirTime = customer.timezone ? timeInZone(customer.timezone) : null;

  return (
    <Screen width="wide">
      <Title sub="Customer">Mulaqaat</Title>

      <Row className="crumbs">
        <Link to="/customers" className="btn btn--ghost">
          ← All customers
        </Link>
      </Row>

      <Card>
        <div className="rec__head">
          <div>
            <Heading>{customer.name}</Heading>
            <p className="rec__phone">{formatPhone(customer.phone)}</p>
          </div>
          <Row>
            {customer.city ? <Chip tone="neutral">{customer.city}</Chip> : null}
            {customer.family_label ? <Chip tone="gold">{customer.family_label}</Chip> : null}
            {/* Guest-time tag is sapphire — informational (§2, §5). */}
            {theirTime ? <Chip tone="info">Their time: {theirTime}</Chip> : null}
          </Row>
        </div>

        <Row className="rec__actions">
          <a
            className="btn btn--primary"
            href={waMeLink(customer.phone, `Sat Sri Akal ${customer.name} ji, Arjun Jewellers ton.`)}
            target="_blank"
            rel="noreferrer noopener"
          >
            Message on WhatsApp
          </a>
          <Button variant="secondary" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Edit details'}
          </Button>
        </Row>
      </Card>

      {editing ? (
        <EditDetails
          customer={customer}
          isAdmin={isAdmin}
          onDone={() => {
            setEditing(false);
            void load();
          }}
        />
      ) : null}

      <Family customerId={customer.id} family={family} hasCluster={!!customer.family_cluster_id} onChanged={load} />

      <Card>
        <Heading>Calls</Heading>
        {calls.length === 0 ? (
          <EmptyState
            line={
              data.calls_filtered_to_own
                ? 'None of your calls with them yet. Calls arrive in Phase 5.'
                : 'No calls yet. Calls arrive in Phase 5.'
            }
          />
        ) : (
          <ul className="calls">
            {calls.map((call) => (
              <li key={call.id} className="calls__row">
                <span className="calls__when">{new Date(call.started_at).toLocaleString()}</span>
                <span className="calls__who">{call.staff_name}</span>
                {call.outcome ? <Chip tone="neutral">{call.outcome}</Chip> : null}
                {call.note ? <span className="calls__note">{call.note}</span> : null}
              </li>
            ))}
          </ul>
        )}
        {data.calls_filtered_to_own ? (
          <p className="rec__hint">You see your own calls. An admin sees everyone’s.</p>
        ) : null}
      </Card>

      <Card>
        <Heading>Invite</Heading>
        <p className="rec__hint" style={{ marginTop: 0, marginBottom: 14 }}>
          Book a Mulaqaat and send them a Nyauta.
        </p>
        <Row>
          <Link to={`/book?customer=${customer.id}`} className="btn btn--primary">
            New Nyauta
          </Link>
        </Row>
      </Card>
    </Screen>
  );
}

function EditDetails({
  customer,
  isAdmin,
  onDone,
}: {
  customer: Customer;
  isAdmin: boolean;
  onDone: () => void;
}) {
  const [name, setName] = useState(customer.name);
  const [city, setCity] = useState(customer.city ?? '');
  const [timezone, setTimezone] = useState(customer.timezone ?? '');
  const [phone, setPhone] = useState(customer.phone);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const zones = useRef(timezoneOptions()).current;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/customers/${customer.id}`, {
        method: 'POST',
        body: {
          name,
          city: city || null,
          timezone: timezone || null,
          // Only send a phone change when there is one — the server refuses it
          // for non-admins, and there is no reason to trip that on every save.
          ...(isAdmin && phone !== customer.phone ? { phone } : {}),
        },
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save those changes.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Heading level={3}>Edit details</Heading>
      <form onSubmit={submit} noValidate>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} maxLength={80} />

        <div className="field">
          <label className="field__label" htmlFor="tz-edit">
            Their timezone
          </label>
          <select id="tz-edit" className="field__input" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            <option value="">Not set</option>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        {isAdmin ? (
          <TextField
            label="Phone number"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            hint="This is how the shop recognises them. Changing it is recorded."
          />
        ) : (
          <div className="field">
            <span className="field__label">Phone number</span>
            <p className="rec__locked">
              {formatPhone(customer.phone)} — only an admin can change this.
            </p>
          </div>
        )}

        {error ? (
          <p className="field__error" role="alert" style={{ marginBottom: 14 }}>
            {error}
          </p>
        ) : null}

        <Button type="submit" busy={busy} disabled={!name.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </form>
    </Card>
  );
}

function Family({
  customerId,
  family,
  hasCluster,
  onChanged,
}: {
  customerId: string;
  family: FamilyMember[];
  hasCluster: boolean;
  onChanged: () => void;
}) {
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CustomerSummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!picking) return;
    const t = setTimeout(async () => {
      try {
        const res = await api<{ customers: CustomerSummary[] }>(
          `/api/customers?q=${encodeURIComponent(q)}&limit=20`
        );
        setResults(res.customers.filter((r) => r.id !== customerId));
      } catch {
        setResults([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, picking, customerId]);

  async function link(otherId: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/customers/${customerId}/link`, { method: 'POST', body: { other_id: otherId } });
      setPicking(false);
      setQ('');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not link them.');
    } finally {
      setBusy(false);
    }
  }

  async function detach() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/customers/${customerId}/detach`, { method: 'POST' });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not detach them.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Heading>Family</Heading>

      {family.length === 0 ? (
        <EmptyState line="Nobody linked yet. Family members show up here once linked." />
      ) : (
        <ul className="fam">
          {family.map((m) => (
            <li key={m.id}>
              <Link to={`/customers/${m.id}`} className="fam__item">
                <span className="fam__name">{m.name}</span>
                <span className="fam__phone">{formatPhone(m.phone)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : null}

      <Row className="rec__actions">
        <Button variant="secondary" onClick={() => setPicking((v) => !v)} disabled={busy}>
          {picking ? 'Cancel' : 'Add someone to this family'}
        </Button>
        {hasCluster ? (
          <Button variant="ghost" onClick={() => void detach()} busy={busy}>
            Remove from this family
          </Button>
        ) : null}
      </Row>

      {picking ? (
        <div className="picker">
          <TextField
            label="Find a customer"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name or number"
            autoComplete="off"
          />
          {!results ? (
            <Loading label="Searching…" />
          ) : results.length === 0 ? (
            <EmptyState line="Nobody else matches. Add them as a customer first." />
          ) : (
            <ul className="picker__list">
              {results.map((r) => (
                <li key={r.id}>
                  <button type="button" className="picker__item" onClick={() => void link(r.id)} disabled={busy}>
                    <span>{r.name}</span>
                    <span className="picker__phone">{formatPhone(r.phone)}</span>
                    {r.family_label ? <Chip tone="gold">{r.family_label}</Chip> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="rec__hint">
            If they are already in another family, the two families are joined into this one.
          </p>
        </div>
      ) : null}
    </Card>
  );
}
